import React from "react";
import { Box, Typography, Avatar, Paper, Grid, Divider } from "@mui/material";
import PropTypes from "prop-types";
import { CommentOutlined, PersonOutlined } from "@mui/icons-material";
import RfiFooter from "./RfiFooter";
import ActionModal from "./ActionModal";
import theme from "../../../../utils/theme";
import useRFIFooter from "../../../../hooks/useRFIFooter";

// Styles object
const styles = {
  remarkCard: {
    minHeight: "80px",
    p: 2,
    borderRadius: 2,
    borderLeft: `3px solid`,
    boxShadow: `0 2px 8px`,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    overflow: "hidden",
  },
  avatar: {
    width: 24,
    height: 24,
    fontSize: "0.7rem",
    flexShrink: 0,
  },
  roleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // mb: 0.5,
  },
  roleText: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: "0.65rem",
    flexShrink: 0,
  },
  remarkText: {
    color: theme.palette.text.primary,
    fontSize: "0.8rem",
    lineHeight: 1.3,
    fontStyle: "italic",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textWrap: "auto",
  },
  paper: {
    mt: 3,
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[200]}`,
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  },
  container: {
    p: 3,
    display: "flex",
    flexDirection: { xs: "column", lg: "row" },
    gap: 3,
    alignItems: { xs: "stretch", lg: "center" },
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
  },
  emptyState: {
    p: 3,
    borderRadius: 3,
    border: `2px dashed ${theme.palette.grey[300]}`,
    textAlign: "left",
    maxHeight: "80px",
    display: "flex",
    alignItems: "center",
    justifyContent: "left",
    gap: 2,
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[200]} 100%)`,
  },
};

/**
 * RemarkCard - Reusable component for displaying QC remarks
 * @param {string} role - Role name (e.g., "QC Coordinator", "QC Manager")
 * @param {string} remark - The remark text
 * @param {string} timestamp - Formatted timestamp
 * @param {string} roleColor - Color for the role text
 * @param {string} gradientColors - Gradient colors for background
 * @param {string} borderColor - Border color
 * @param {string} avatarGradient - Avatar gradient colors
 * @param {number} slideTimeout - Animation timeout
 */
const RemarkCard = ({
  role,
  remark,
  timestamp,
  roleColor,
  gradientColors,
  borderColor,
  avatarGradient,
  userName = "user name",
  showAction,
}) => (
  <Box
    sx={{
      ...styles.remarkCard,
      background: gradientColors,
      border: `1px solid ${borderColor}`,
      borderLeft: `3px solid ${borderColor}`,
      boxShadow: `0 2px 8px ${borderColor}20`,
      width: "100%",
    }}
  >
    <Box sx={{ alignItems: "center", gap: 1, width: "100%" }}>
      <Grid
        container
        sx={{
          alignItems: "center",
          spacing: 1,
          justifyContent: "left",
          mb: 1,
          width: "100%",
        }}
        spacing={2}
      >
        <Grid size={{ xs: 2, lg: 1.2 }}>
          <Avatar
            sx={{
              ...styles.avatar,
              background: avatarGradient,
            }}
          >
            <PersonOutlined sx={{ fontSize: 12 }} />
          </Avatar>
        </Grid>
        <Grid size={{ xs: 10, lg: 10.8 }} sx={{ display: "flex" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                ...styles.roleText,
                color: roleColor,
              }}
            >
              {role}
            </Typography>
            <Typography variant="caption" sx={{ ...styles.roleText }}>
              {userName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={styles.timestamp}>
              {timestamp}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Divider />
      <Box sx={{ width: "100%", mt: 1.5 }}>
        <Typography variant="body2" sx={styles.remarkText}>
          "{remark}"
        </Typography>
      </Box>
    </Box>
  </Box>
);

/**
 * StageFooter - Component for the bottom section of each stage with remarks and action buttons
 * @param {function} onSendForApproval - Handler for Send for approval button
 * @param {object} sx - Additional styling for the footer container
 * @param {boolean} isSubmitting - Loading state to show spinner on Send for approval
 * @param {boolean} showAction - Whether to show action buttons
 * @param {boolean} showSubmitButton - Whether to show submit button
 * @param {string} submitButtonText - Text for submit button
 * @param {boolean} awaitingReview - Whether to show awaiting review message
 * @param {object} stage - Stage object containing remarks and other details
 */
const StageFooter = ({
  onSendForApproval,
  sx = {},
  isSubmitting = false,
  showAction = true,
  showSubmitButton = true,
  submitButtonText = "Send for approval to QC Coordinator",
  stage,
}) => {
  const {
    actionModalOpen,
    actionRemark,
    modalActionType,
    rfiDetails,
    handleRejectClick,
    handleApproveClick,
    handleConfirmAction,
    handleCancelAction,
    setActionRemark,
  } = useRFIFooter(stage.stage_id);

  return (
    <Paper elevation={1} sx={{ ...styles.paper, ...sx }}>
      <Box sx={styles.container}>
        {/* Left side - Timeline Remarks */}
        <Grid
          container
          spacing={2}
          sx={{
            width: showAction ? { xs: "100%", lg: "50%" } : { xs: "100%" },
          }}
        >
          {stage.coordinator_remarks && (
            <Grid size={showAction ? { xs: 12 } : { xs: 6 }}>
              <RemarkCard
                role={`QC Coordinator`}
                remark={stage.coordinator_remarks || ""}
                userName={
                  rfiDetails?.qc_coordinator?.first_name +
                  " " +
                  rfiDetails?.qc_coordinator?.last_name
                }
                timestamp={
                  stage.coordinator_reviewed_at
                    ? new Date(
                        stage.coordinator_reviewed_at
                      ).toLocaleDateString()
                    : "n/a"
                }
                roleColor={
                  (stage.current_step === "Inspector" ||
                    stage.current_step === "Coordinator") &&
                  stage.manager_remarks &&
                  !stage.manager_approved
                    ? theme.palette.success.main
                    : stage.coordinator_remarks && stage.coordinator_approved
                    ? theme.palette.success.main
                    : theme.palette.error.main
                }
                gradientColors={
                  (stage.current_step === "Inspector" ||
                    stage.current_step === "Coordinator") &&
                  stage.manager_remarks &&
                  !stage.manager_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.light}20 0%, ${theme.palette.success.light}10 100%)`
                    : stage.coordinator_remarks && stage.coordinator_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.light}20 0%, ${theme.palette.success.light}10 100%)`
                    : `linear-gradient(135deg, ${theme.palette.error.main}10 0%, ${theme.palette.error.light}10 100%)`
                }
                borderColor={
                  (stage.current_step === "Inspector" ||
                    stage.current_step === "Coordinator") &&
                  stage.manager_remarks &&
                  !stage.manager_approved
                    ? theme.palette.success.light
                    : stage.coordinator_remarks && stage.coordinator_approved
                    ? theme.palette.success.light
                    : theme.palette.error.light
                }
                avatarGradient={
                  (stage.current_step === "Inspector" ||
                    stage.current_step === "Coordinator") &&
                  stage.manager_remarks &&
                  !stage.manager_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                    : stage.coordinator_remarks && stage.coordinator_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                }
                slideTimeout={600}
                showAction={showAction}
              />
            </Grid>
          )}

          {stage.manager_remarks && (
            <Grid size={showAction ? { xs: 12 } : { xs: 6 }}>
              <RemarkCard
                role={`QC Manager`}
                userName={
                  rfiDetails?.qc_manager?.first_name +
                  " " +
                  rfiDetails?.qc_manager?.last_name
                }
                remark={stage.manager_remarks || ""}
                timestamp={
                  stage.manager_reviewed_at
                    ? new Date(stage.manager_reviewed_at).toLocaleDateString()
                    : "n/a"
                }
                roleColor={
                  stage.manager_remarks && stage.manager_approved
                    ? theme.palette.success.main
                    : theme.palette.error.main
                }
                gradientColors={
                  stage.manager_remarks && stage.manager_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.light}20 0%, ${theme.palette.success.light}10 100%)`
                    : `linear-gradient(135deg, ${theme.palette.error.main}10 0%, ${theme.palette.error.light}10 100%)`
                }
                borderColor={
                  stage.manager_remarks && stage.manager_approved
                    ? theme.palette.success.light
                    : theme.palette.error.light
                }
                avatarGradient={
                  stage.manager_remarks && stage.manager_approved
                    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                }
                slideTimeout={800}
                showAction={showAction}
              />
            </Grid>
          )}
          {!stage.coordinator_remarks &&
          !stage.manager_remarks &&
          (stage.current_step === "Inspector" ||
            stage.current_step === "Product_Engineer") ? (
            <Grid
              sx={styles.emptyState}
              size={showAction ? { xs: 12 } : { xs: 6 }}
            >
              <CommentOutlined
                sx={{ fontSize: 24, color: theme.palette.grey[400] }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    fontSize: "0.85rem",
                  }}
                >
                  {"No remarks"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.grey[400],
                    fontSize: "0.7rem",
                  }}
                >
                  No remarks yet
                </Typography>
              </Box>
            </Grid>
          ) : stage.current_step === "Coordinator" &&
            !stage.coordinator_remarks ? (
            <Grid
              sx={styles.emptyState}
              size={showAction ? { xs: 12 } : { xs: 6 }}
            >
              <CommentOutlined
                sx={{ fontSize: 24, color: theme.palette.grey[400] }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    fontSize: "0.85rem",
                  }}
                >
                  {"Awaiting Review"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.grey[400],
                    fontSize: "0.7rem",
                  }}
                >
                  No remarks yet by QC Coordinator.
                </Typography>
              </Box>
            </Grid>
          ) : (
            stage.current_step === "Manager" &&
            !stage.manager_remarks && (
              <Grid
                sx={styles.emptyState}
                size={showAction ? { xs: 12 } : { xs: 6 }}
              >
                <CommentOutlined
                  sx={{ fontSize: 24, color: theme.palette.grey[400] }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      fontSize: "0.85rem",
                    }}
                  >
                    {"Awaiting Review"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.grey[400],
                      fontSize: "0.7rem",
                    }}
                  >
                    No remarks yet by QC Manager
                  </Typography>
                </Box>
              </Grid>
            )
          )}
        </Grid>

        {/* Right side - Action Buttons */}
        {showAction && (
          <Grid
            container
            sx={{
              width: showAction && { xs: "100%", lg: "50%" },
              display: !showAction && "none",
            }}
            spacing={2}
          >
            <RfiFooter
              onReject={handleRejectClick}
              onApprove={handleApproveClick}
              onSendForApproval={onSendForApproval}
              isLoading={isSubmitting}
              showSubmitButton={showSubmitButton}
              submitButtonText={submitButtonText}
              showAction={showAction}
            />
          </Grid>
        )}
      </Box>

      {/* Action Modal */}
      <ActionModal
        open={actionModalOpen}
        onClose={handleCancelAction}
        actionType={modalActionType}
        remark={actionRemark}
        onRemarkChange={(e) => setActionRemark(e.target.value)}
        onConfirm={handleConfirmAction}
      />
    </Paper>
  );
};

StageFooter.propTypes = {
  showAction: PropTypes.bool,
  showSubmitButton: PropTypes.bool,
  onSendForApproval: PropTypes.func,
  isSubmitting: PropTypes.bool,
  sx: PropTypes.object,
  submitButtonText: PropTypes.string,
  awaitingReview: PropTypes.bool,
  stage: PropTypes.object,
};

export default StageFooter;
